import { useContext, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { FullMetadata } from 'firebase/storage'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import ContentViewerErrorState from '@/content-viewer/components/common/ContentViewerErrorState'
import MarkdownRenderer from '@/content-viewer/components/file/MarkdownRenderer'
import PDFBaseRenderer from '@/content-viewer/components/base-renderers/PDFBaseRenderer'
import { RenderFormat, type FileContentRendererConfig } from '@/content-viewer/store/schemas'
import { AuthContext } from '@/context/auth-context'
import { useTitle } from '@/hooks/use-title'
import { brandedAIFriendlyName } from '@/util/enterprise'
import { getFileMetadataFromGCS } from '@/routes/dashboard/files/utils/files-utils'
import { buildLegacyFileRendererConfig } from '@/routes/dashboard/files/utils/build-legacy-file-renderer-config'
import { resolveReferenceStorage } from '@/chat-common/reference-storage/resolve-reference-storage'
import type { PaxtonHighlightArea } from '@/components/react-pdf-viewer/schema/paxton-highlight-area'

const EMPTY_PDF_HIGHLIGHTS: PaxtonHighlightArea[] = []
const EMPTY_MARKDOWN_HIGHLIGHTS: string[] = []

type LegacyFileViewerState = {
  fileMetadata: FullMetadata | null
  renderer: FileContentRendererConfig | null
  isLoading: boolean
  errorMessage: string | null
}

function buildLegacyFullPath(uid: string, relativePath: string | null): string | null {
  const cleanedPath = relativePath?.trim().replace(/^\/+/, '')
  if (!cleanedPath) return null

  return `users/${uid}/${cleanedPath}`
}

function LegacyFileReferenceContent({ renderer, title }: { renderer: FileContentRendererConfig; title: string }) {
  const storageConfig = resolveReferenceStorage({
    path: renderer.filePath,
    format: renderer.format === RenderFormat.PDF ? 'pdf' : 'markdown',
  })
  const filePath = storageConfig.normalizedPath ?? renderer.filePath

  if (renderer.format === RenderFormat.PDF) {
    return (
      <PDFBaseRenderer
        filePath={filePath}
        gcsBucketRef={storageConfig.bucketRef}
        legacy_always_cloud={storageConfig.legacy_always_cloud}
        highlights={renderer.highlights ?? EMPTY_PDF_HIGHLIGHTS}
        startingPage={renderer.startingPage}
        title={title}
      />
    )
  }

  return (
    <div className="h-full w-full overflow-y-auto">
      <MarkdownRenderer
        filePath={filePath}
        gcsBucketName={storageConfig.bucketRef}
        legacy_always_cloud={storageConfig.legacy_always_cloud}
        highlightSentences={renderer.highlights ?? EMPTY_MARKDOWN_HIGHLIGHTS}
      />
    </div>
  )
}

export default function StandaloneReferenceViewerLegacyFilePage() {
  const [searchParams] = useSearchParams()
  const { user, loading: authLoading } = useContext(AuthContext)
  const relativePath = searchParams.get('viewFile')
  const [state, setState] = useState<LegacyFileViewerState>({
    fileMetadata: null,
    renderer: null,
    isLoading: true,
    errorMessage: null,
  })

  const title = state.fileMetadata?.name ?? `${brandedAIFriendlyName} Reference Viewer`
  useTitle(title)

  useEffect(() => {
    if (authLoading) return

    let cancelled = false

    async function loadFile() {
      const fullPath = user?.uid ? buildLegacyFullPath(user.uid, relativePath) : null
      if (!fullPath) {
        setState({
          fileMetadata: null,
          renderer: null,
          isLoading: false,
          errorMessage: 'Could not locate this file.',
        })
        return
      }

      setState({
        fileMetadata: null,
        renderer: null,
        isLoading: true,
        errorMessage: null,
      })

      try {
        const fileMetadata = await getFileMetadataFromGCS(fullPath)
        const renderer = buildLegacyFileRendererConfig(fileMetadata)

        if (!renderer) {
          throw new Error('Unsupported legacy file preview type')
        }

        if (!cancelled) {
          setState({
            fileMetadata,
            renderer,
            isLoading: false,
            errorMessage: null,
          })
        }
      } catch {
        if (!cancelled) {
          setState({
            fileMetadata: null,
            renderer: null,
            isLoading: false,
            errorMessage: 'Could not load this file.',
          })
        }
      }
    }

    loadFile()

    return () => {
      cancelled = true
    }
  }, [authLoading, relativePath, user?.uid])

  return (
    <div className="grow flex flex-col h-full overflow-y-scroll pb-4 pt-8">
      {state.isLoading && (
        <div className="flex justify-center items-center flex-1">
          <CircularProgressContinuousSized size={25} thickness={5} />
        </div>
      )}

      {state.errorMessage && !state.isLoading && (
        <div className="pl-2 h-full">
          <ContentViewerErrorState message={state.errorMessage} />
        </div>
      )}

      {state.fileMetadata && state.renderer && !state.isLoading && !state.errorMessage && (
        <div className="w-full max-w-5xl mx-auto flex flex-col h-full min-h-0 gap-4">
          <div className="pl-2 flex items-center justify-between gap-4">
            <h1 className="text-xl font-bold leading-7 truncate text-brand-neutral-950">{state.fileMetadata.name}</h1>
          </div>

          <div className="min-h-0 flex-1 bg-white border border-brand-neutral-200">
            <LegacyFileReferenceContent renderer={state.renderer} title={state.fileMetadata.name} />
          </div>
        </div>
      )}
    </div>
  )
}
